import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, MessageBar, MessageBarBody, Spinner, Switch, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import type { MailLogEntry, MailSettingsView, OpeningSettingsView, SsoSettingsView } from '@vxh/shared';
import { api } from '../api';
import { PageHeader } from '../components/PageHeader';
import { ago, fullTime } from '../format';

const useStyles = makeStyles({
  body: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('16px'), ...shorthands.padding('20px') },
  row: { display: 'flex', ...shorthands.gap('12px'), alignItems: 'flex-start', flexWrap: 'wrap' },
  grow: { flex: 1, minWidth: '240px' },
  stack: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('4px') },
  muted: { color: tokens.colorNeutralForeground3 },
  tableWrap: { overflowX: 'auto' },
});

/** Hub-wide settings. For now: the mail service instances can use, and a record of what they have sent through it. */
export function SettingsPage() {
  return (
    <>
      <PageHeader title="Settings" subtitle="Services the hub provides to the instances connected to it." />
      <OpeningCard />
      <div style={{ height: 16 }} />
      <SsoCard />
      <div style={{ height: 16 }} />
      <MailCard />
      <div style={{ height: 16 }} />
      <MailLog />
    </>
  );
}

/** How Open chooses between an installation's own address and the hub's tunnel. */
function OpeningCard() {
  const s = useStyles();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['opening-settings'], queryFn: () => api<OpeningSettingsView>('/opening/settings') });
  const [error, setError] = useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: (probeDirect: boolean) => api<OpeningSettingsView>('/opening/settings', { method: 'PUT', body: { probeDirect } }),
    onSuccess: (v) => (setError(null), qc.setQueryData(['opening-settings'], v)),
    onError: (e) => setError((e as Error).message),
  });
  if (q.isLoading) return <Card><Spinner label="Loading" /></Card>;
  if (q.isError || !q.data) return <MessageBar intent="error"><MessageBarBody>{(q.error as Error)?.message ?? 'Could not load the settings.'}</MessageBarBody></MessageBar>;
  return (
    <Card>
      <div className={s.body}>
        <div className={s.stack}>
          <Text weight="semibold">Opening instances</Text>
          <Text className={s.muted}>
            When someone clicks Open, the hub opens the installation at its own address if they are on the same network as it, and otherwise through the hub (when the installation allows that).
            With the switch below on, the person's browser first tests whether the installation's own address answers from where they are, and uses it if it does. With it off, only
            "same network" decides.
          </Text>
        </div>
        <Switch checked={q.data.probeDirect} disabled={toggle.isPending} onChange={(_, d) => toggle.mutate(d.checked)} label="Test the installation's own address from the browser first" />
        <Text className={s.muted}>
          The test is a small request your browser sends to the installation's address (https addresses only); the hub never makes it and nothing is sent to the hub about it. Some browsers (Chrome) may ask
          each person once for permission to "access devices on your local network" the first time they open an installation on a private address. If they decline, Open simply goes through the hub.
        </Text>
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      </div>
    </Card>
  );
}

/** Single sign-on: the hub as the sign-in provider for instances. The hub-wide kill switch and the signing key. (Each instance has its own switch in its details.) */
function SsoCard() {
  const s = useStyles();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['sso-settings'], queryFn: () => api<SsoSettingsView>('/sso/settings') });
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = (v: SsoSettingsView) => (setError(null), setConfirmRotate(false), qc.setQueryData(['sso-settings'], v));
  const toggle = useMutation({ mutationFn: (enabled: boolean) => api<SsoSettingsView>('/sso/settings', { method: 'PUT', body: { enabled } }), onSuccess: done, onError: (e) => setError((e as Error).message) });
  const rotate = useMutation({ mutationFn: () => api<SsoSettingsView>('/sso/keys/rotate', { method: 'POST', body: {} }), onSuccess: done, onError: (e) => (setConfirmRotate(false), setError((e as Error).message)) });
  if (q.isLoading) return <Card><Spinner label="Loading" /></Card>;
  if (q.isError || !q.data) return <MessageBar intent="error"><MessageBarBody>{(q.error as Error)?.message ?? 'Could not load the settings.'}</MessageBarBody></MessageBar>;
  const v = q.data;
  return (
    <Card>
      <div className={s.body}>
        <div className={s.stack}>
          <Text weight="semibold">Single sign-on</Text>
          <Text className={s.muted}>
            Lets people sign in to a Voxshift instance with their hub account (password and authenticator code), with no second sign-in. The hub vouches for the person; each instance decides for itself
            whether to trust it, and its customer can lower the role it gives. Which instances a person may sign in to, and as what, is set under People.
          </Text>
        </div>
        <Switch checked={v.enabled} disabled={toggle.isPending} onChange={(_, d) => toggle.mutate(d.checked)} label={v.enabled ? 'Single sign-on is on' : 'Single sign-on is off for every instance'} />
        {!v.enabled && <MessageBar intent="warning"><MessageBarBody>Nobody can sign in to any instance with a hub account until this is switched back on. Instances keep working with their own sign-in.</MessageBarBody></MessageBar>}
        <div className={s.stack}>
          <Text weight="semibold">Address instances use</Text>
          <Text>{v.issuer}</Text>
          <Text className={s.muted}>Its discovery document: {v.discoveryUrl}</Text>
        </div>
        <div className={s.stack}>
          <Text weight="semibold">Signing key</Text>
          <Text className={s.muted}>
            Every sign-in is signed with this key and checked by the instance. It is replaced automatically every 90 days. Replace it now if you think it may have been exposed: the old one stays valid for ten
            minutes so sign-ins in progress are not lost.
          </Text>
          <div className={s.tableWrap}>
            <Table aria-label="Signing keys">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Key</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {v.keys.map((k) => (
                  <TableRow key={k.kid}>
                    <TableCell>{k.kid}</TableCell>
                    <TableCell title={fullTime(k.createdAt)}>{ago(k.createdAt)}</TableCell>
                    <TableCell>{k.active ? <Badge appearance="tint" color="success" shape="rounded">In use</Badge> : <Badge appearance="outline" color="subtle" shape="rounded">{k.retiredAt && Date.now() - Date.parse(k.retiredAt) < 10 * 60_000 ? 'Retiring' : 'Retired'}</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
        <div className={s.row}>
          <Button onClick={() => setConfirmRotate(true)}>Replace the signing key</Button>
        </div>
      </div>
      {confirmRotate && (
        <Dialog open onOpenChange={(_, d) => !d.open && setConfirmRotate(false)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Replace the signing key?</DialogTitle>
              <DialogContent>New sign-ins use a new key straight away. Instances fetch the new key by themselves; sign-ins already in progress keep working for about ten minutes.</DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setConfirmRotate(false)}>Cancel</Button>
                <Button appearance="primary" disabled={rotate.isPending} onClick={() => rotate.mutate()}>Replace</Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </Card>
  );
}

function MailCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['mail-settings'], queryFn: () => api<MailSettingsView>('/mail/settings') });
  if (q.isLoading) return <Card><Spinner label="Loading" /></Card>;
  if (q.isError || !q.data) return <MessageBar intent="error"><MessageBarBody>{(q.error as Error)?.message ?? 'Could not load the settings.'}</MessageBarBody></MessageBar>;
  // Remount when the saved settings change so the form always shows what is stored.
  return <MailForm key={JSON.stringify({ ...q.data, passwordSet: q.data.passwordSet })} v={q.data} onSaved={(v) => qc.setQueryData<MailSettingsView>(['mail-settings'], v)} />;
}

function MailForm({ v, onSaved }: { v: MailSettingsView; onSaved: (v: MailSettingsView) => void }) {
  const s = useStyles();
  const [enabled, setEnabled] = useState(v.enabled);
  const [host, setHost] = useState(v.host);
  const [port, setPort] = useState(String(v.port));
  const [secure, setSecure] = useState(v.secure);
  const [user, setUser] = useState(v.user);
  const [password, setPassword] = useState('');
  const [fromAddress, setFromAddress] = useState(v.fromAddress);
  const [fromName, setFromName] = useState(v.fromName);
  const [hourly, setHourly] = useState(String(v.hourlyLimit));
  const [daily, setDaily] = useState(String(v.dailyLimit));
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [test, setTest] = useState<{ ok: boolean; message: string } | null>(null);
  const touch = <T,>(set: (x: T) => void) => (x: T) => (set(x), setDirty(true));

  const portNum = Number(port);
  const valid = Number.isInteger(portNum) && portNum >= 1 && portNum <= 65535 && Number(hourly) >= 1 && Number(daily) >= 1 && (!enabled || (host.trim() && /^\S+@\S+\.\S+$/.test(fromAddress.trim())));

  const save = useMutation({
    mutationFn: () =>
      api<MailSettingsView>('/mail/settings', {
        method: 'PUT',
        body: { enabled, host: host.trim(), port: portNum, secure, user: user.trim(), ...(password ? { password } : {}), fromAddress: fromAddress.trim(), fromName: fromName.trim() || 'Voxshift', hourlyLimit: Number(hourly), dailyLimit: Number(daily) },
      }),
    onSuccess: (r) => {
      setError(null);
      setDirty(false);
      onSaved(r);
    },
    onError: (e) => setError((e as Error).message),
  });
  const sendTest = useMutation({
    mutationFn: () => api('/mail/test', { method: 'POST', body: { to: testTo.trim() } }),
    onSuccess: () => setTest({ ok: true, message: `Sent to ${testTo.trim()}. Check the inbox (and junk mail).` }),
    onError: (e) => setTest({ ok: false, message: (e as Error).message }),
  });

  return (
    <Card>
      <div className={s.body}>
        <div className={s.stack}>
          <Text weight="semibold">Mail service</Text>
          <Text className={s.muted}>
            Lets every instance connected to the hub send its email through the hub, so a customer-premises installation needs no mail server of its own. The hub sends from the address below. It passes
            messages on and keeps none of them: the log below records the instance, the recipient's domain, the size and whether it was sent.
          </Text>
        </div>
        <Switch checked={enabled} onChange={(_, d) => touch(setEnabled)(d.checked)} label={enabled ? 'The mail service is on' : 'The mail service is off'} />
        <div className={s.row}>
          <Field className={s.grow} label="Mail server" hint="For Microsoft 365: smtp.office365.com">
            <Input value={host} onChange={(_, d) => touch(setHost)(d.value)} />
          </Field>
          <Field label="Port" hint="587 or 465" style={{ width: 120 }}>
            <Input value={port} onChange={(_, d) => touch(setPort)(d.value)} inputMode="numeric" />
          </Field>
        </div>
        <Switch checked={secure} onChange={(_, d) => touch(setSecure)(d.checked)} label={secure ? 'Encrypted from the start (port 465)' : 'Upgrade to encryption (STARTTLS, port 587). Messages are never sent unencrypted.'} />
        <div className={s.row}>
          <Field className={s.grow} label="User name">
            <Input value={user} onChange={(_, d) => touch(setUser)(d.value)} autoComplete="off" />
          </Field>
          <Field className={s.grow} label="Password" hint={v.passwordSet ? 'A password is saved. Type here only to replace it.' : undefined}>
            <Input type="password" value={password} onChange={(_, d) => touch(setPassword)(d.value)} autoComplete="new-password" placeholder={v.passwordSet ? '••••••••' : ''} />
          </Field>
        </div>
        <div className={s.row}>
          <Field className={s.grow} label="Send from" hint="The address every relayed message comes from. It must be allowed on the mail server above.">
            <Input value={fromAddress} onChange={(_, d) => touch(setFromAddress)(d.value)} />
          </Field>
          <Field className={s.grow} label="Default sender name" hint="Instances can show their own name with it.">
            <Input value={fromName} onChange={(_, d) => touch(setFromName)(d.value)} />
          </Field>
        </div>
        <div className={s.row}>
          <Field label="Most per instance, per hour" style={{ width: 200 }}>
            <Input value={hourly} onChange={(_, d) => touch(setHourly)(d.value)} inputMode="numeric" />
          </Field>
          <Field label="Most per instance, per day" style={{ width: 200 }}>
            <Input value={daily} onChange={(_, d) => touch(setDaily)(d.value)} inputMode="numeric" />
          </Field>
        </div>
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
        <div className={s.row}>
          <Button appearance="primary" disabled={!dirty || !valid || save.isPending} onClick={() => save.mutate()}>Save</Button>
        </div>

        <div className={s.stack}>
          <Text weight="semibold">Send a test email</Text>
          <div className={s.row}>
            <Field className={s.grow} label="To" hint={dirty ? 'Save your changes first: the test uses the saved settings.' : undefined}>
              <Input type="email" value={testTo} onChange={(_, d) => setTestTo(d.value)} />
            </Field>
            <Button style={{ marginTop: 22 }} disabled={dirty || !/^\S+@\S+\.\S+$/.test(testTo.trim()) || sendTest.isPending} onClick={() => sendTest.mutate()}>
              {sendTest.isPending ? 'Sending' : 'Send test'}
            </Button>
          </div>
          {test && <MessageBar intent={test.ok ? 'success' : 'error'}><MessageBarBody>{test.message}</MessageBarBody></MessageBar>}
        </div>
      </div>
    </Card>
  );
}

function MailLog() {
  const s = useStyles();
  const q = useQuery({ queryKey: ['mail-log'], queryFn: () => api<MailLogEntry[]>('/mail/log?limit=100'), refetchInterval: 30_000 });
  return (
    <Card>
      <div className={s.body}>
        <div className={s.stack}>
          <Text weight="semibold">Recently relayed</Text>
          <Text className={s.muted}>One line per message an instance handed to the hub. Kept for 30 days. No subject, address or content is recorded.</Text>
        </div>
        {q.isLoading ? (
          <Spinner label="Loading" />
        ) : (q.data ?? []).length === 0 ? (
          <Text className={s.muted}>Nothing has been relayed yet.</Text>
        ) : (
          <div className={s.tableWrap}>
            <Table aria-label="Relayed mail">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>When</TableHeaderCell>
                  <TableHeaderCell>Instance</TableHeaderCell>
                  <TableHeaderCell>To domain</TableHeaderCell>
                  <TableHeaderCell>Size</TableHeaderCell>
                  <TableHeaderCell>Result</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(q.data ?? []).map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{fullTime(m.at)}</TableCell>
                    <TableCell>{m.instanceName}</TableCell>
                    <TableCell>{m.toDomain}</TableCell>
                    <TableCell>{Math.max(1, Math.round(m.bytes / 1024))} kB</TableCell>
                    <TableCell title={m.error ?? undefined}>
                      <Badge appearance={m.status === 'sent' ? 'tint' : 'outline'} color={m.status === 'sent' ? 'success' : m.status === 'failed' ? 'danger' : 'warning'} shape="rounded" style={{ whiteSpace: 'nowrap' }}>
                        {m.status === 'sent' ? 'Sent' : m.status === 'failed' ? 'Failed' : 'Refused (limit)'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Card>
  );
}
